import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { isValidPhoneNumber } from "libphonenumber-js";
import _ from "lodash";
import { useUpdateInterview } from "../../../../../store/query/scheduleInterviewQuery";
import { useToast } from "../../../../../contexts-hooks/hooks/useToster";
import { useNavigate } from "react-router-dom";
import CommonSnackbar from "../../../../../components/common-components/CommonSnackbar";
import { useAllUserList } from "../../../../../store/query/userQuery";
import { debounce } from "lodash";
import SvgCamera from "../../../../../assets/Icons/svg-icon-component/SvgCamera";
import classNames from "classnames";
import { useGetAllRoles } from "../../../../../store/query/roleQuery";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LANGUAGE_OPTIONS } from "../../../../../constants/languages";

const BE_TO_FE_INTERVIEW_TYPE = {
  "AI Bot": "ai_interview",
  "Coding Test": "coding_assessment",
  "AI Bot +": "ai_bot_plus",
};

const useEditCandidateInfo = (selectedInterviewData) => {
  const [onChangeValidation, setOnChangeValidation] = useState(false);
  const allRoles = useGetAllRoles();
  const inputRef = useRef(null);

  const [placeHolderOpacity, setPlaceHolderOpacity] = useState(0);
  const [profilePic, setProfilePic] = useState(
    selectedInterviewData?.data?.interview.profilePic || ""
  );
  const [clearProfilePic, setClearProfilePic] = useState(false);
  const [allRecruiterList, setAllRecruiterList] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    limit: 10,
    name: "",
  });
  const [recruiter, setRecruiter] = useState(null);
  const useUpdateInterviewMutation = useUpdateInterview();
  const [isUpdating, setIsUpdating] = useState(false);

  const allUserList = useAllUserList({
    page: filterOptions.page,
    limit: filterOptions.limit,
    search: filterOptions.name,
  });
  const [startDate, setStartDate] = useState(null);
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);
  const [phomeErrorMessage, setPhomeErrorMessage] = useState(null);
  const [fieldChanged, setFieldChanged] = useState({});
  const { showToast } = useToast();
  useEffect(() => {
    if (selectedInterviewData?.data?.interview) {
      setStartDate(new Date(selectedInterviewData?.data?.interview?.startTime));
    }
  }, [selectedInterviewData]);

  const formik = useFormik({
    initialValues: {
      candidateName:
        selectedInterviewData?.data?.interview?.candidateName || "",
      candidateEmail:
        selectedInterviewData?.data?.interview?.candidateEmail || "",
      candidatePhone:
        selectedInterviewData?.data?.interview?.candidatePhone || "",
      profilePic: selectedInterviewData?.data?.interview?.profilePic || "",
      applyFor: selectedInterviewData?.data?.role[0] || null,
      resume:
        selectedInterviewData?.data?.resumeData?.metadata?.originalname || "",
      jobDescription:
        selectedInterviewData?.data?.jobDescriptionData?.metadata
          ?.originalname || "",
      startTime: selectedInterviewData?.data?.interview?.startTime || null,
      endTime: selectedInterviewData?.data?.interview?.endTime || null,
      street:
        selectedInterviewData?.data?.interview?.candidateAddress?.street || "",
      city:
        selectedInterviewData?.data?.interview?.candidateAddress?.city || "",
      state:
        selectedInterviewData?.data?.interview?.candidateAddress?.state || "",
      zip: selectedInterviewData?.data?.interview?.candidateAddress?.zip || "",
      recruiter: selectedInterviewData?.data?.interview?.recruiter || "",
      recruiterEmail:
        selectedInterviewData?.data?.interview?.recruiterEmail || "",
      useCustomQuestion:
        selectedInterviewData?.data?.interview?.useCustomQuestion || "",
      experienceLevel:
        selectedInterviewData?.data?.interview?.experienceLevel || "",
      interviewLanguage:
        selectedInterviewData?.data?.interview?.interviewLanguage || "en",
      interviewType:
        BE_TO_FE_INTERVIEW_TYPE[selectedInterviewData?.data?.interview?.interviewType] ||
        selectedInterviewData?.data?.interview?.interviewType || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      candidateName: Yup.string()
        .required("Candidate Name is required")
        .max(50, "Name should not exceed 50 characters"),
      candidateEmail: Yup.string()
        .email("Invalid email address")
        .required("Email address is required"),
      candidatePhone: Yup.string().required("Phone number is required"),
      recruiter: Yup.string()
        .required("Candidate Name is required")
        .max(200, "Name should not exceed 150 characters"),
      recruiterEmail: Yup.string()
        .email("Invalid email address")
        .required("Email address is required"),
      applyFor: Yup.object().required("Job Description is required"),
      useCustomQuestion: Yup.string().required("Please select Yes or No"),
      resume: Yup.string()
        .required("Resume Title is required")
        .max(200, "Title should not exceed 150 characters"),
      jobDescription: Yup.mixed().required("Job Description is required"),
      profilePic: Yup.mixed().nullable(),
      startTime: Yup.date()
        .min(new Date(), "Start Time cannot be in the past")
        .required("Start Time is required"),
    }),
    validateOnChange: onChangeValidation,
    validateOnBlur: true,
    onSubmit: (values) => {
      handleUploadResume(values);
    },
  });
  const uploadProfilePicClasses = classNames({
    "upload-placeholder": true,
    "upload-placeholder_hover": !profilePic,
    "opacity-0": profilePic && placeHolderOpacity === 0,
    "opacity-1": profilePic && placeHolderOpacity === 1,
  });

  const handleSetProfilePic = (file) => {
    formik.setFieldValue("profilePic", file);
    setFieldChanged((prev) => ({
      ...prev,
      profilePic: file,
    }));
  };

  useEffect(() => {
    if (allUserList.data?.data) {
      setAllRecruiterList((prev) => [...prev, ...allUserList.data.data]);
    }
  }, [allUserList.data]);
  const debouncedFilterChange = useCallback(
    debounce((value) => {
      setAllRecruiterList([]);
      setFilterOptions((prev) => ({
        ...prev,
        page: 1,
        name: value,
      }));
    }, 1000),
    []
  );
  const handleInputChange = (event, value) => {
    if (!value) {
      setAllRecruiterList([]);
      setFilterOptions((prev) => ({
        ...prev,
        page: 1,
        name: "",
      }));
    } else {
      debouncedFilterChange(value);
    }
  };
  const handleGetMoreUserOnScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - 50
    ) {
      if (!allUserList.isLoading) {
        setFilterOptions((prev) => ({
          ...prev,
          page: prev.page + 1,
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedInterviewData?.data?.interview?.recruiterEmail) {
    }
    setRecruiter(selectedInterviewData?.data?.recruiter[0], () => {
      setFilterOptions((prev) => ({
        ...prev,
        name: "",
      }));
    });
    setProfilePic(selectedInterviewData?.data?.interview?.profilePic);
  }, [selectedInterviewData]);

  const now = new Date();

  const validatePhoneNumber = (phone, country) => {
    const phoneNumber = phone.slice(country.dialCode.length);
    formik.setFieldValue(
      "candidatePhone",
      "+" + country.dialCode + " " + phoneNumber
    );
    setFieldChanged((prev) => ({
      ...prev,
      candidatePhone: "+" + country.dialCode + " " + phoneNumber,
    }));
    if (phone.length === 0) {
      setPhomeErrorMessage("Phone number is required");
      return;
    }
    const isValid = isValidPhoneNumber(
      "+" + country.dialCode + " " + phoneNumber,
      country.countryCode.toUpperCase()
    );
    if (!isValid) {
      setValidPhoneNumber(false);
      setPhomeErrorMessage("Invalid phone number");
    } else {
      setPhomeErrorMessage(null);
      setValidPhoneNumber(true);
    }
  };

  const handleCandidateInterview = (e) => {
    const { value, name } = e.target;
    formik.setFieldValue(name, value);
    setFieldChanged((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleUploadResume = (values) => {
    setIsUpdating(true);

    const formData = new FormData();
    _.forIn(fieldChanged, (value, key) => {
      if (key === "applyFor") {
        formData.append(key, value.role);
      } else if (key === "recruiter") {
        formData.append("recruiter", value.recruiter);
        formData.append("recruiterEmail", value.recruiterEmail);
      } else if (
        key === "profilePic" &&
        value !== null &&
        typeof value !== "string"
      ) {
        formData.append("profilePic", value);
      } else if (key === "profilePic" && value === null) {
        formData.append("profilePic", "");
        formData.append("removeProfilePic", "true");
      } else {
        formData.append(key, value);
      }
    });
    const { _id } = selectedInterviewData?.data?.interview;
    useUpdateInterviewMutation.mutate(
      { _id, formData },
      {
        onSuccess: (data) => {
          setIsUpdating(false);
          showToast("Interview details updated successfully!");
          navigateToList("/app/interview/list-schedule-interview");
        },
        onError: (error) => {
          setIsUpdating(false);
          showToast(
            "There was an error updating the interview details.",
            "error"
          );
        },
      }
    );
  };
  const navigateToList = useNavigate();
  const handleNavigateCandidateList = () => {
    navigateToList("/app/interview/list-schedule-interview");
  };

  return {
    formik,
    handleCandidateInterview,
    handleUploadResume,
    handleNavigateCandidateList,
    handleInputChange,
    handleGetMoreUserOnScroll,
    handleSetProfilePic,
    uploadProfilePicClasses,
    profilePic,
    setProfilePic,
    selectedInterviewData,
    allRoles,
    allRecruiterList,
    recruiter,
    setRecruiter,
    allUserList,
    startDate,
    setStartDate,
    now,
    validatePhoneNumber,
    validPhoneNumber,
    phomeErrorMessage,
    useUpdateInterviewMutation,
    inputRef,
    clearProfilePic,
    setFieldChanged,
    isUpdating,
    levelOptions: [
      { value: "Junior", label: "Junior" },
      { value: "Middle", label: "Middle" },
      { value: "Senior", label: "Senior" },
      { value: "Leadership", label: "Leadership" },
    ],
    languageOptions: LANGUAGE_OPTIONS,
    interviewTypeOptions: [
      { value: "ai_interview", label: "AI Interview" },
      { value: "coding_assessment", label: "Coding Assessment" },
      { value: "ai_bot_plus", label: "AI Bot+" },
    ],
  };
};

export default useEditCandidateInfo;
